import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Blocking modal shown once when a user is authenticated but has not yet
 * accepted the current DISCLAIMER_VERSION. Used for the Google sign-up path
 * and any legacy account that predates the disclaimer feature.
 * Password sign-ups accept via a checkbox on the Register form and never
 * see this modal.
 */
export default function DisclaimerModal() {
  const { user, acknowledgeDisclaimer } = useAuth();
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Only render if logged in and not yet acknowledged
  if (!user || user.disclaimer_acknowledged) return null;

  // Don't stack modals — let the welcome/profile flow finish first
  if (!user.profile_seen) return null;

  const onAccept = async () => {
    if (!checked) return;
    setSubmitting(true);
    const res = await acknowledgeDisclaimer();
    setSubmitting(false);
    if (!res.ok) {
      toast.error(res.error || "Couldn't save your acknowledgment. Try again.");
      return;
    }
    toast.success("Thanks — you're all set.");
  };

  return (
    <div
      data-testid="disclaimer-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#2D2926]/60 backdrop-blur-sm"
    >
      <div className="bg-[#FBF9F6] max-w-lg w-full rounded-3xl shadow-2xl border border-[#E6E2D8] max-h-[92vh] overflow-y-auto">
        <div className="px-8 pt-8 pb-2">
          <p className="text-xs uppercase tracking-[0.3em] text-[#9A938A] mb-3">
            One quick thing
          </p>
          <h2 className="font-serif text-3xl text-[#2D2926] leading-tight">
            Before you dive in — a note about what Colleague is
          </h2>
          <p className="mt-4 text-sm text-[#3D3833] leading-relaxed">
            Colleague personas are AI. They can be warm and often helpful, but
            they can also be wrong. Nothing they say is professional advice.
          </p>
          <p className="mt-3 text-sm text-[#3D3833] leading-relaxed">
            Please don&apos;t use Colleague as a substitute for a real HR
            professional, therapist, doctor, lawyer, or financial advisor —
            especially for high-stakes decisions. You are always the
            decision-maker.
          </p>
        </div>

        <div className="px-8 pt-4 pb-8 space-y-4">
          <label
            htmlFor="disclaimer-modal-checkbox"
            className="flex items-start gap-3 cursor-pointer text-[13px] text-[#3D3833] leading-relaxed bg-[#F7F3EA] border border-[#E6DFCF] rounded-xl px-4 py-3"
          >
            <input
              id="disclaimer-modal-checkbox"
              type="checkbox"
              data-testid="disclaimer-modal-checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1 h-4 w-4 accent-[#C29260] shrink-0"
            />
            <span>
              I understand persona replies are AI-generated opinions, not
              professional advice. I&apos;ve read the{" "}
              <Link
                to="/disclaimer"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="disclaimer-modal-link"
                className="text-[#2D2926] hover:text-[#C29260] underline underline-offset-4"
              >
                full AI Advice Disclaimer
              </Link>
              .
            </span>
          </label>

          <button
            type="button"
            onClick={onAccept}
            disabled={!checked || submitting}
            data-testid="disclaimer-modal-accept"
            className="w-full bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-6 py-3 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "Saving…" : "I understand — let's go"}
          </button>
        </div>
      </div>
    </div>
  );
}
